<template>
  <div class="register">
    <div
      class="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8"
    >
      <div class="max-w-md w-full space-y-8 bg-card rounded-xl border border-border shadow-sm p-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-foreground">
            {{ $t('auth.createAccountTitle') }}
          </h2>
          <p class="mt-2 text-center text-sm text-muted-foreground">
            {{ $t('auth.or') }}
            <router-link
              :to="localePath('Login')"
              class="font-medium text-primary hover:text-primary/80"
            >
              {{ $t('auth.signInExisting') }}
            </router-link>
          </p>
        </div>

        <AlertBanner v-if="!settingsStore.anyRegistrationEnabled" variant="warning" class="mt-8">
          <p class="text-sm">
            {{ $t('auth.registrationDisabled') }}
            <router-link
              :to="localePath('Login')"
              class="font-medium text-primary hover:text-primary/80"
              >{{ $t('auth.signIn') }}</router-link
            >
            {{ $t('auth.ifHaveAccount') }}
          </p>
        </AlertBanner>

        <AlertBanner v-else-if="success" variant="success" class="mt-8">
          <p class="text-sm">
            {{ $t('auth.registrationSuccess') }}
          </p>
        </AlertBanner>

        <template v-else>
          <form
            v-if="settingsStore.emailRegistrationEnabled"
            class="mt-8 space-y-6"
            novalidate
            @submit.prevent="handleRegister"
          >
            <AlertBanner v-if="error" variant="error">
              <p class="text-sm">{{ error }}</p>
            </AlertBanner>

            <div class="space-y-4">
              <div>
                <label for="username" class="block text-sm font-medium text-foreground mb-1">{{
                  $t('auth.usernameLabel')
                }}</label>
                <input
                  id="username"
                  v-model="username"
                  type="text"
                  :class="[
                    'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                    fieldErrors.username
                      ? 'border-destructive focus:ring-destructive focus:border-destructive'
                      : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                  ]"
                  :placeholder="$t('auth.usernamePlaceholder')"
                />
                <p v-if="fieldErrors.username" class="mt-1 text-sm text-destructive">
                  {{ fieldErrors.username }}
                </p>
              </div>
              <div>
                <label for="email" class="block text-sm font-medium text-foreground mb-1">{{
                  $t('auth.emailLabel')
                }}</label>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  :class="[
                    'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                    fieldErrors.email
                      ? 'border-destructive focus:ring-destructive focus:border-destructive'
                      : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                  ]"
                  :placeholder="$t('auth.emailContactPlaceholder')"
                />
                <p v-if="fieldErrors.email" class="mt-1 text-sm text-destructive">
                  {{ fieldErrors.email }}
                </p>
              </div>
              <div>
                <label for="password" class="block text-sm font-medium text-foreground mb-1">{{
                  $t('auth.passwordLabel')
                }}</label>
                <input
                  id="password"
                  v-model="password"
                  type="password"
                  :class="[
                    'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                    fieldErrors.password
                      ? 'border-destructive focus:ring-destructive focus:border-destructive'
                      : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                  ]"
                  :placeholder="$t('auth.passwordMinLength')"
                />
                <p v-if="fieldErrors.password" class="mt-1 text-sm text-destructive">
                  {{ fieldErrors.password }}
                </p>
              </div>
              <div>
                <label
                  for="password-confirmation"
                  class="block text-sm font-medium text-foreground mb-1"
                  >{{ $t('auth.confirmPasswordLabel') }}</label
                >
                <input
                  id="password-confirmation"
                  v-model="passwordConfirmation"
                  type="password"
                  :class="[
                    'appearance-none relative block w-full px-3 py-2 border placeholder-muted-foreground text-foreground rounded-md focus:outline-none sm:text-sm',
                    fieldErrors.passwordConfirmation
                      ? 'border-destructive focus:ring-destructive focus:border-destructive'
                      : 'border-input bg-input-background focus:ring-ring focus:border-ring',
                  ]"
                  :placeholder="$t('auth.confirmPasswordPlaceholder')"
                />
                <p v-if="fieldErrors.passwordConfirmation" class="mt-1 text-sm text-destructive">
                  {{ fieldErrors.passwordConfirmation }}
                </p>
              </div>
              <TermsConsentCheckbox v-model="termsAccepted" :error="fieldErrors.terms" />
            </div>

            <div>
              <button
                type="submit"
                :disabled="loading || !termsAccepted"
                class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LoadingSpinner :loading="loading" />
                {{ $t('auth.register') }}
              </button>
            </div>
          </form>

          <div v-if="settingsStore.anyOAuthEnabled" class="mt-6">
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-border"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-background text-muted-foreground">{{
                  settingsStore.emailRegistrationEnabled
                    ? $t('auth.orContinueWith')
                    : $t('auth.continueWith')
                }}</span>
              </div>
            </div>

            <div
              :class="['mt-6 grid gap-3', enabledOAuthCount > 1 ? 'grid-cols-2' : 'grid-cols-1']"
            >
              <button
                v-if="settingsStore.googleAuthEnabled"
                type="button"
                class="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted cursor-pointer"
                @click="handleOAuthRegister('google')"
              >
                <svg class="h-5 w-5" viewBox="0 0 48 48">
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                  />
                </svg>
              </button>
              <button
                v-if="settingsStore.facebookAuthEnabled"
                type="button"
                class="w-full inline-flex justify-center py-2 px-4 border border-border rounded-md shadow-sm bg-card text-sm font-medium text-foreground hover:bg-muted cursor-pointer"
                @click="handleOAuthRegister('facebook')"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path
                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '../../api/client'
import { useSettingsStore } from '../../stores/settings'
import { useLocalePath } from '../../composables/useLocalePath'
import { usePageTitle } from '../../composables/usePageTitle'
import { initiateOAuth } from '../../utils/oauth'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import AlertBanner from '../../components/AlertBanner.vue'
import TermsConsentCheckbox from '../../components/TermsConsentCheckbox.vue'

const { t, locale } = useI18n()
usePageTitle(() => t('titles.register'))
const settingsStore = useSettingsStore()
const { localePath } = useLocalePath()

const enabledOAuthCount = computed(
  () => (settingsStore.googleAuthEnabled ? 1 : 0) + (settingsStore.facebookAuthEnabled ? 1 : 0),
)

onMounted(() => {
  settingsStore.fetchSettings()
})

const username = ref('')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const termsAccepted = ref(false)
const loading = ref(false)
const error = ref(null)
const success = ref(false)
const fieldErrors = reactive({})

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const clearFieldErrors = () => {
  for (const key of Object.keys(fieldErrors)) delete fieldErrors[key]
}

const validate = () => {
  clearFieldErrors()

  if (!username.value.trim()) {
    fieldErrors.username = t('auth.usernameRequired')
  }

  if (!email.value.trim()) {
    fieldErrors.email = t('auth.emailRequired')
  } else if (!emailRegex.test(email.value.trim())) {
    fieldErrors.email = t('auth.emailInvalid')
  }

  if (!password.value) {
    fieldErrors.password = t('auth.passwordRequired')
  } else if (password.value.length < 8) {
    fieldErrors.password = t('auth.passwordTooShort')
  }

  if (!passwordConfirmation.value) {
    fieldErrors.passwordConfirmation = t('auth.confirmPasswordRequired')
  } else if (password.value && password.value !== passwordConfirmation.value) {
    fieldErrors.passwordConfirmation = t('auth.passwordsMismatch')
  }

  if (!termsAccepted.value) {
    fieldErrors.terms = t('auth.termsRequired')
  }

  return Object.keys(fieldErrors).length === 0
}

const mapServerErrors = (errors) => {
  const fieldI18nMap = {
    Username: { field: 'username', key: 'auth.usernameAlreadyTaken' },
    Email: { field: 'email', key: 'auth.emailAlreadyTaken' },
    'Password confirmation': {
      field: 'passwordConfirmation',
      key: 'auth.passwordConfirmationMismatch',
    },
    Password: { field: 'password', key: 'auth.passwordTooShortServer' },
    Terms: { field: 'terms', key: 'auth.termsRequired' },
  }

  for (const message of errors) {
    let mapped = false
    for (const [prefix, mapping] of Object.entries(fieldI18nMap)) {
      if (message.startsWith(prefix)) {
        fieldErrors[mapping.field] = t(mapping.key)
        mapped = true
        break
      }
    }
    if (!mapped) {
      error.value = t('common.somethingWentWrong')
    }
  }
}

watch(username, () => delete fieldErrors.username)
watch(email, () => delete fieldErrors.email)
watch(password, () => delete fieldErrors.password)
watch(passwordConfirmation, () => delete fieldErrors.passwordConfirmation)
watch(termsAccepted, () => delete fieldErrors.terms)

const handleRegister = async () => {
  error.value = null
  success.value = false
  if (!validate()) return

  loading.value = true

  try {
    await apiClient.post('/auth/register', {
      user: {
        username: username.value,
        email: email.value,
        password: password.value,
        password_confirmation: passwordConfirmation.value,
        preferred_locale: locale.value,
        accept_terms: termsAccepted.value,
      },
    })
    success.value = true
  } catch (error_) {
    const data = error_.response?.data
    if (data?.errors && Array.isArray(data.errors)) {
      clearFieldErrors()
      mapServerErrors(data.errors)
    } else {
      error.value = t(
        data?.error === 'registration_disabled'
          ? 'auth.registrationDisabledError'
          : 'auth.registrationFailed',
      )
    }
  } finally {
    loading.value = false
  }
}

const handleOAuthRegister = (provider) => {
  initiateOAuth(provider)
}
</script>
